import { WeatherData, WeatherForecast } from '../types/weather';

// Open-Meteo API endpoints
const OPEN_METEO_BASE_URL = 'https://api.open-meteo.com/v1/forecast';
const OPEN_METEO_GEO_URL = 'https://geocoding-api.open-meteo.com/v1/search';

export class WeatherApiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WeatherApiError';
  }
}

// Function to get coordinates from location name
async function getCoordinates(location: string): Promise<{ lat: number; lon: number; name: string }> {
  try {
    const url = `${OPEN_METEO_GEO_URL}?name=${encodeURIComponent(location)}&count=1`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new WeatherApiError(`Geocoding API Error: ${response.status} - ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (!data.results || data.results.length === 0) {
      throw new WeatherApiError(`Location not found: ${location}`);
    }
    
    const result = data.results[0];
    return {
      lat: result.latitude,
      lon: result.longitude,
      name: result.name
    };
  } catch (error) {
    if (error instanceof WeatherApiError) {
      throw error;
    }
    throw new WeatherApiError(error instanceof Error ? error.message : 'Failed to geocode location');
  }
}

export const fetchWeatherByLocation = async (location: string): Promise<{
  current: WeatherData;
  forecast: WeatherForecast;
}> => {
  try {
    // First get coordinates from location name
    const coordinates = await getCoordinates(location);
    
    // Get current language from localStorage or default to English
    const currentLang = localStorage.getItem('i18nextLng') || 'en';
    
    // Fetch weather data from Open-Meteo
    const url = `${OPEN_METEO_BASE_URL}?latitude=${coordinates.lat}&longitude=${coordinates.lon}&daily=temperature_2m_max,temperature_2m_min,precipitation_sum,windspeed_10m_max,weathercode&current_weather=true&timezone=auto&forecast_days=6&language=${currentLang}`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new WeatherApiError(`API Error: ${response.status} - ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (!data || !data.daily || !data.current_weather) {
      throw new WeatherApiError('Invalid API response format');
    }
    
    // Map weather codes to descriptions
    const weatherCodeDescriptions: Record<number, string> = {
      0: 'Clear sky',
      1: 'Mainly clear',
      2: 'Partly cloudy',
      3: 'Overcast',
      45: 'Fog',
      48: 'Depositing rime fog',
      51: 'Light drizzle',
      53: 'Moderate drizzle',
      55: 'Dense drizzle',
      56: 'Light freezing drizzle',
      57: 'Dense freezing drizzle',
      61: 'Slight rain',
      63: 'Moderate rain',
      65: 'Heavy rain',
      66: 'Light freezing rain',
      67: 'Heavy freezing rain',
      71: 'Slight snow fall',
      73: 'Moderate snow fall',
      75: 'Heavy snow fall',
      77: 'Snow grains',
      80: 'Slight rain showers',
      81: 'Moderate rain showers',
      82: 'Violent rain showers',
      85: 'Slight snow showers',
      86: 'Heavy snow showers',
      95: 'Thunderstorm',
      96: 'Thunderstorm with slight hail',
      99: 'Thunderstorm with heavy hail'
    };
    
    // Format current weather
    const current: WeatherData = {
      temperature: Math.round(data.current_weather.temperature),
      humidity: 0, // Open-Meteo doesn't provide humidity in free tier
      rainfall: 0, // Will be calculated from daily data
      windSpeed: Math.round(data.current_weather.windspeed),
      description: weatherCodeDescriptions[data.current_weather.weathercode] || 'Unknown',
      date: new Date().toISOString()
    };
    
    // If today's precipitation data is available, use it for current rainfall
    if (data.daily.precipitation_sum && data.daily.precipitation_sum.length > 0) {
      current.rainfall = data.daily.precipitation_sum[0] || 0;
    }
    
    // Format 5-day forecast (skip today)
    const daily = [];
    
    for (let i = 1; i < Math.min(data.daily.time.length, 6); i++) {
      daily.push({
        temperature: Math.round((data.daily.temperature_2m_max[i] + data.daily.temperature_2m_min[i]) / 2),
        humidity: 0, // Open-Meteo doesn't provide humidity in free tier
        rainfall: data.daily.precipitation_sum[i] || 0,
        windSpeed: Math.round(data.daily.windspeed_10m_max[i] || 0),
        description: weatherCodeDescriptions[data.daily.weathercode[i]] || 'Unknown',
        date: new Date(data.daily.time[i]).toISOString()
      });
    }

    return {
      current,
      forecast: {
        daily,
        location: {
          name: coordinates.name,
          lat: coordinates.lat,
          lon: coordinates.lon
        }
      }
    };
  } catch (error) {
    console.error('Error fetching weather:', error);
    throw error instanceof WeatherApiError ? error : new WeatherApiError(
      error instanceof Error ? error.message : 'Failed to fetch weather data'
    );
  }
};